import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { trace, context, propagation } from '@opentelemetry/api';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { logger } from '@/lib/logger';

let isInitialized = false;

export function initTelemetry() {
  if (isInitialized) {
    logger.debug('Telemetry already initialized, skipping');
    return;
  }

  // Skip telemetry in development if no endpoint configured
  if (!import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT && import.meta.env.DEV) {
    logger.debug('Skipping telemetry initialization in development (no endpoint configured)');
    return;
  }

  logger.info('Initializing OpenTelemetry');

  try {
    // Check if OTLP endpoint is configured
    if (!import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT) {
      logger.warn('Telemetry export disabled (no OTLP endpoint configured)');
      
      // Still register provider and propagator for trace context propagation
      const provider = new WebTracerProvider();
      provider.register({
        contextManager: new ZoneContextManager(),
        propagator: new W3CTraceContextPropagator(),
      });
      propagation.setGlobalPropagator(new W3CTraceContextPropagator());
      
      isInitialized = true;
      logger.info('Context propagation enabled (export disabled)');
      return;
    }

    // OTLP endpoint configured - full setup
    const otlpEndpoint = import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT;
    const otlpHeaders = import.meta.env.VITE_OTEL_EXPORTER_OTLP_HEADERS || '';
    
    const exporter = new OTLPTraceExporter({
      url: otlpEndpoint,
      headers: otlpHeaders ? { Authorization: otlpHeaders } : undefined,
    });

    // Create batch span processor
    const spanProcessor = new BatchSpanProcessor(exporter, {
      maxQueueSize: 100,
      maxExportBatchSize: 10,
      scheduledDelayMillis: 500,
    });

    // Create tracer provider with span processor
    const provider = new WebTracerProvider({
      spanProcessors: [spanProcessor]
    });

    // Register provider
    provider.register({
      contextManager: new ZoneContextManager(),
      propagator: new W3CTraceContextPropagator(),
    });

    // Set global propagator
    propagation.setGlobalPropagator(new W3CTraceContextPropagator());

    // Register instrumentations
    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: [
            /.+/g, // Propagate to all URLs (adjust in production)
          ],
          clearTimingResources: true,
          applyCustomAttributesOnSpan: (span, request) => {
            // Add custom attributes
            const url = typeof request === 'string' ? request : (request as any).url || '';
            if (url.includes('supabase')) {
              span.setAttribute('supabase.request', true);
            }
          },
        }),
      ],
    });

    isInitialized = true;
    logger.info('OpenTelemetry initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize OpenTelemetry', error instanceof Error ? error : new Error(String(error)));
  }
}

/**
 * Get the active tracer instance
 */
export function getTracer(name = 'ybr-frontend') {
  return trace.getTracer(name);
}

/**
 * Create a new span and execute a function within its context
 */
export async function traceAsync<T>(
  name: string,
  fn: () => Promise<T>,
  attributes?: Record<string, string | number | boolean>
): Promise<T> {
  const tracer = getTracer();
  return tracer.startActiveSpan(name, async (span) => {
    try {
      // Add custom attributes
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }

      const result = await fn();
      span.setStatus({ code: 1 }); // OK
      return result;
    } catch (error) {
      span.setStatus({
        code: 2, // ERROR
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      span.recordException(error as Error);
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Create a sync span
 */
export function traceSync<T>(
  name: string,
  fn: () => T,
  attributes?: Record<string, string | number | boolean>
): T {
  const tracer = getTracer();
  return tracer.startActiveSpan(name, (span) => {
    try {
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          span.setAttribute(key, value);
        });
      }

      const result = fn();
      span.setStatus({ code: 1 });
      return result;
    } catch (error) {
      span.setStatus({
        code: 2,
        message: error instanceof Error ? error.message : 'Unknown error',
      });
      span.recordException(error as Error);
      throw error;
    } finally {
      span.end();
    }
  });
}

/**
 * Get current trace context headers for propagation
 */
export function getTraceHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  propagation.inject(context.active(), headers);
  return headers;
}

/**
 * Add event to current span
 */
export function addSpanEvent(name: string, attributes?: Record<string, string | number>) {
  const span = trace.getActiveSpan();
  if (span) {
    span.addEvent(name, attributes);
  }
}

/**
 * Set attribute on current span
 */
export function setSpanAttribute(key: string, value: string | number | boolean) {
  const span = trace.getActiveSpan();
  if (span) {
    span.setAttribute(key, value);
  }
}
